import Dialog from '@material-ui/core/Dialog';
import { useMediaQuery } from 'common/utils';
import Button from 'components/common/Button';
import Icon from 'components/common/Icon';
import { useContext, useEffect, useMemo, useState } from 'react';
import { QuestionType, answerAQuestion, LecturePaidType } from 'service/course';
import { CourseDetailsContext } from '..';
import AnswerItemView from '../AnswerItemView';
import AnswersResult from './AnswersResult';

import styles from './QuestionsAnswerDialog.module.scss';

const QuestionAnswserDialog = () => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<{
    [questionId: string]: number;
  }>({});
  const {
    openQuestionAnswerDialog,
    setOpenQuestionAnswerDialog,
    courseInfo,
    currentLecture,
    setCurrentLecture,
    refetchCourse,
    setOpenCoursePaywall,
  } = useContext(CourseDetailsContext);
  const [isShowingResult, setIsShowingResult] = useState(false);
  const currentLectureIndex = useMemo(() => {
    if (!currentLecture) return -1;
    return (courseInfo.lectures || []).findIndex(
      (lec) => lec.id === currentLecture?.id
    );
  }, [courseInfo, currentLecture]);

  useEffect(() => {
    if (openQuestionAnswerDialog) {
      setCurrentQuestionIndex(0);
      setUserAnswers({});
      if (currentLecture?.questions?.some((ques) => !!ques.user_answer_id)) {
        setIsShowingResult(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openQuestionAnswerDialog]);
  const handleAnswerAQuestion = async (callback: () => void) => {
    try {
      await answerAQuestion({
        courseId: courseInfo.id.toString(),
        lectureId: currentLecture?.id?.toString(),
        questionId: currentLecture?.questions[currentQuestionIndex]?.id,
        answerId:
          userAnswers[
            currentLecture?.questions[currentQuestionIndex]?.id?.toString()
          ],
      });
      callback();
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  const handleClose = () => {
    refetchCourse();
    setOpenQuestionAnswerDialog(false);
  };
  if (!currentLecture) return <></>;
  return (
    <Dialog
      open={openQuestionAnswerDialog}
      maxWidth="lg"
      scroll="body"
      fullScreen={isMobile}
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <div className={styles.dialogWrapper}>
        <div className={styles.closeBtn}>
          <Icon
            name="close"
            width={28}
            height={28}
            onClick={() => handleClose()}
          />
        </div>
        <div className={styles.container}>
          {isShowingResult ? (
            <>
              <AnswersResult />
              <div className={styles.actions}>
                <div className={styles.backBtn}>
                  <Button
                    text="もう一度挑戦する"
                    onClick={() => {
                      setCurrentQuestionIndex(0);
                      setUserAnswers({});
                      setIsShowingResult(false);
                    }}
                  />
                </div>
                {currentLectureIndex < courseInfo.lectures.length - 1 && (
                  <div className={styles.nextBtn}>
                    <Button
                      text="次のレクチャーへ"
                      onClick={() => {
                        handleClose();
                        if (
                          currentLectureIndex >=
                          courseInfo.lectures.length - 1
                        )
                          return;
                        const newLectureIndex = currentLectureIndex + 1;
                        const newLecture = courseInfo.lectures[newLectureIndex];
                        if (newLecture.type === LecturePaidType.Free) {
                          setCurrentLecture(newLecture);
                          return;
                        }
                        // if can watch
                        if (courseInfo.is_owner || courseInfo.is_purchased) {
                          setCurrentLecture(newLecture);
                        } else {
                          // if can't watch => open paywall
                          setOpenCoursePaywall && setOpenCoursePaywall(true);
                          return;
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className={styles.questionsCount}>
                クイズ {currentQuestionIndex + 1}/
                {currentLecture?.questions?.length}
              </div>
              <CurrentQuestionView
                question={currentLecture?.questions[currentQuestionIndex]}
                userAnswers={userAnswers}
                setUserAnswers={setUserAnswers}
              />
              <div className={styles.actions}>
                {currentQuestionIndex > 0 && (
                  <div className={styles.backBtn}>
                    <Button
                      text="前のクイズを見る"
                      onClick={() => {
                        if (currentQuestionIndex > 0) {
                          setCurrentQuestionIndex(currentQuestionIndex - 1);
                        }
                      }}
                    />
                  </div>
                )}
                <div className={styles.nextBtn}>
                  <Button
                    text={
                      currentQuestionIndex ===
                      currentLecture?.questions?.length - 1
                        ? `解答して結果を見る`
                        : `解答して次へ`
                    }
                    disabled={
                      currentLecture?.questions[
                        currentQuestionIndex
                      ]?.id?.toString() in userAnswers
                        ? false
                        : true
                    }
                    onClick={() => {
                      if (
                        currentQuestionIndex <
                        currentLecture?.questions?.length - 1
                      ) {
                        void handleAnswerAQuestion(() => {
                          setCurrentQuestionIndex(currentQuestionIndex + 1);
                        });
                      } else if (
                        currentQuestionIndex ===
                        currentLecture?.questions?.length - 1
                      ) {
                        void handleAnswerAQuestion(() => {
                          refetchCourse();
                          setIsShowingResult(true);
                        });
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

const CurrentQuestionView = ({
  question,
  userAnswers,
  setUserAnswers,
}: {
  question: QuestionType;
  userAnswers: { [questionId: string]: number };
  setUserAnswers: (a: { [questionId: string]: number }) => void;
}) => {
  if (!question) return <></>;
  const onChooseAnswer = (answersId: number) => {
    setUserAnswers({
      ...userAnswers,
      [question.id.toString()]: answersId,
    });
  };
  return (
    <div className={styles.currentQuestionWrapper}>
      <div className={styles.questionDetail}>{question.detail}</div>
      <div className={styles.answersList}>
        {question.answers.map((answer, index) => {
          const isSelected = userAnswers[question.id.toString()] === answer.id;
          return (
            <AnswerItemView
              onChooseAnswer={onChooseAnswer}
              key={`answer-${index}`}
              type={isSelected ? 'selected' : 'normal'}
              answerItem={answer}
              answerIndex={index}
            />
          );
        })}
      </div>
    </div>
  );
};

export default QuestionAnswserDialog;
